import {capitalize, Stack, Typography, useTheme} from '@mui/material';
import {Drawer} from 'presentation/components/Drawer';
import {Timeline} from 'presentation/components/Timeline';
import {translations} from 'presentation/translations-new';
import React, {Fragment, useState} from 'react';
import {AlertInfo, TagChip} from 'presentation/components';
import {Snackbar} from 'presentation/components/Snackbar';
import Icon from 'presentation/icons';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {PRODUCT_LINK_OMBUDSMAN} from 'presentation/constants';
import {useHostName} from 'presentation/hooks/useHostName';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {AuthorizationStatusEnum, cardStyleByStatus, Status} from '../../constants';
import {getAttendanceProtocol, getInternalProtocol} from '../../utils';
import {useRejectedReanalysisFeatureFlag} from '../../hooks/useRejectedReanalysisFeatureFlag';
import {useAuthStatusDrawerData} from './hooks';
import {AuthStatusDrawerProps} from './types';

const {details, close} = translations['pt-br'].commons;
const {
	reanaliseAlertTitle,
	reanaliseAlertDescription,
	reanaliseButton,
	statusLabel,
	protocolLabel,
	internalProtocolLabel,
	lastUpdateLabel,
	protocolCopySuccess,
	copyProtocolAria,
} = translations['pt-br'].privateFlow.requestAuthorizations.detail;

export function AuthStatusDrawer({
	authorization,
	openDrawer,
	onOpenDrawer,
	onCloseDrawer,
}: AuthStatusDrawerProps) {
	const theme = useTheme();
	const {isMobile} = useMediaQueries();
	const {chipText, chipVariant, chipTextColor, chipBackgroundColor} = cardStyleByStatus(
		theme,
		authorization.dsSituacao as Status,
	);

	const {timelineData, lastUpdate} = useAuthStatusDrawerData(authorization.trilhaStatus);

	const {dataHostNameState} = useHostName();
	const ombudsmanUrl = PRODUCT_LINK_OMBUDSMAN[dataHostNameState.product];

	const {isEnabled: isReanalysisEnabled} = useRejectedReanalysisFeatureFlag();

	const isRejected = authorization.dsSituacao === AuthorizationStatusEnum.REJECTED;
	const showReanalysis = isRejected && isReanalysisEnabled;

	const [showCopySnackbar, setShowCopySnackbar] = useState(false);

	const attendanceProtocol = getAttendanceProtocol(authorization);
	const internalProtocol = getInternalProtocol(authorization);

	const handleRequestReanalise = () => {
		window.open(ombudsmanUrl, '_blank', 'noopener,noreferrer');
	};

	const handleCopyProtocol = () => {
		if (!attendanceProtocol || showCopySnackbar) return;
		navigator.clipboard.writeText(attendanceProtocol);
		setShowCopySnackbar(true);
	};

	const secondaryTextColor = theme.palette.text.secondary;
	const primaryTextColor = theme.palette.text.primary;
	const iconColor = theme.palette.primary.main;

	return (
		<Drawer.Root
			open={openDrawer}
			onClose={onCloseDrawer}
			onOpen={onOpenDrawer}
			width="600px"
			customMaxHeight="80%"
			height={isMobile ? '87%' : '100%'}>
			<Drawer.Header
				closeBtnDataTagId={AutoSolEnum.CLICK_FECHAR_DRAWER_TRILHA_AUTOSOL}
				onClose={onCloseDrawer}
				title={details}
			/>
			<Drawer.Content>
				<Fragment>
					<Stack gap={2}>
						<Typography variant="body2" fontWeight={600} fontSize={18} color="primary">
							{capitalize(authorization.nmProcedimento.toLowerCase())}
						</Typography>

						<Stack gap={1}>
							<Stack direction="row" gap={1} alignItems="flex-start">
								<Icon name="cached-default" size={20} color={iconColor} />
								<Stack gap={0.5}>
									<Typography variant="body2" color={secondaryTextColor}>
										{statusLabel}
									</Typography>
									<TagChip
										label={chipText}
										color={chipVariant}
										usePrimaryTextColor={false}
										sx={{
											width: 'max-content',
											color: chipTextColor,
											backgroundColor: chipBackgroundColor,
											'& .MuiChip-label': {color: chipTextColor},
										}}
									/>
								</Stack>
							</Stack>

							{attendanceProtocol ? (
								<Stack direction="row" gap={1} alignItems="flex-start">
									<Icon name="assignment-filled" size={20} color={iconColor} />
									<Stack gap={0.5}>
										<Typography variant="body2" color={secondaryTextColor}>
											{protocolLabel}
										</Typography>
										<Stack direction="row" gap={0.5} alignItems="center">
											<Typography variant="body1" color={primaryTextColor}>
												{attendanceProtocol}
											</Typography>
											<Icon
												name="copy-default"
												size={20}
												color={iconColor}
												onClick={handleCopyProtocol}
												aria-label={copyProtocolAria}
												role="button"
											/>
										</Stack>
									</Stack>
								</Stack>
							) : null}

							{internalProtocol ? (
								<Stack gap={0.5} pl={3.5}>
									<Typography variant="caption" color={secondaryTextColor}>
										{internalProtocolLabel}
									</Typography>
									<Typography variant="caption" color={secondaryTextColor}>
										{internalProtocol}
									</Typography>
								</Stack>
							) : null}

							{lastUpdate ? (
								<Stack direction="row" gap={1} alignItems="flex-start">
									<Icon name="calendar-clock" size={20} color={iconColor} />
									<Stack gap={0.5}>
										<Typography variant="body2" color={secondaryTextColor}>
											{lastUpdateLabel}
										</Typography>
										<Typography variant="body1" color={primaryTextColor}>
											{lastUpdate}
										</Typography>
									</Stack>
								</Stack>
							) : null}
						</Stack>

						{showReanalysis ? (
							<AlertInfo.Root
								severity="info"
								icon={<Icon name="info-outlined" size={22} />}>
								<AlertInfo.Title title={reanaliseAlertTitle} />
								<AlertInfo.Description>
									<Typography variant="body2">
										{reanaliseAlertDescription}
									</Typography>
								</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}
					</Stack>
					<Timeline content={timelineData} />
				</Fragment>
			</Drawer.Content>
			<Drawer.Footer
				direction="column"
				firstButtonTitle={reanaliseButton}
				firstButtonOnClick={handleRequestReanalise}
				hideFirstButton={!showReanalysis}
				secondButtonDataTagId={AutoSolEnum.CLICK_BOTAO_FECHAR_DRAWER_AUTOSOL}
				secondButtonTitle={close}
				secondButtonOnClick={onCloseDrawer}
			/>
			<Snackbar
				open={showCopySnackbar}
				message={protocolCopySuccess}
				onClose={() => setShowCopySnackbar(false)}
			/>
		</Drawer.Root>
	);
}
